/**
 * invoicingSections — the inner tabs of Account › Financials › Invoicing, and
 * who sees which. AccountInvoicingTab renders from this list and the settings
 * search (utils/settingsSearch) offers its entries from it, so a search result
 * never points at a section the viewer's Invoicing tab would not show.
 *
 * The ids are what the address carries (`account_section=options`); a few
 * friendlier spellings resolve too (resolveInvoicingSection).
 */

import { canOpenInvoices } from '@turn-connect/hooks';
import { canSeeAccounting } from '../invoicing/accounting/accountingAccess';

export type InvoicingSectionId =
  | 'coverage'
  | 'schedules'
  | 'deliveryLists'
  | 'options'
  | 'contacts'
  | 'whoGetsWhat'
  | 'history'
  | 'sentEmails'
  | 'accounting'
  | 'aiCheckup';

export const INVOICING_SECTION_LABELS: Record<InvoicingSectionId, string> = {
  coverage: 'Coverage',
  schedules: 'Schedules',
  deliveryLists: 'Deliveries',
  options: 'Options',
  contacts: 'Contacts',
  whoGetsWhat: 'Who gets what',
  history: 'Invoices',
  sentEmails: 'Emails',
  accounting: 'Accounting',
  aiCheckup: 'Check-up',
};

/** Who may use the Invoicing Advisor's modes (Rule Optimizer, AI Advisor). */
export const advisorPerms = (permissions: string[] = []) => {
  const has = (p: string) => permissions.includes(p);
  const isSysAdmin = has('system:admin');
  const canEditRules = isSysAdmin || has('invoice:rules:edit') || has('account:admin');
  const canUseAIChat = isSysAdmin || has('ai:chat');
  const canUseAIAdvisor = canEditRules && canUseAIChat;
  const canAccessAdvisor = canEditRules;
  return { canEditRules, canUseAIChat, canUseAIAdvisor, canAccessAdvisor };
};

/**
 * The rules surface (Schedules, Deliveries and the document Options) uses one
 * permission for reading and editing — handlers.requireInvoiceRuleAccess on
 * the server.
 */
export const canSeeInvoicingRules = (permissions: string[] = []): boolean =>
  permissions.includes('invoice:rules:edit') || permissions.includes('account:admin');

/** The Invoicing sections this viewer sees, in tab order. */
export function invoicingSectionIds(permissions: string[] = []): InvoicingSectionId[] {
  return [
    // Contacts appear only for an account on the new invoice setup; the tab
    // itself checks that (AccountInvoicingTab), the permission is the rules'.
    // Coverage reads the same rules (and counts who gets them), so it has their permission.
    ...(canSeeInvoicingRules(permissions)
      ? (['coverage', 'schedules', 'deliveryLists', 'options', 'contacts', 'whoGetsWhat'] as const)
      : []),
    // Invoices lists and opens invoices: the people who may view them. Sent
    // emails shows which invoices went to whom, so it follows the same rule.
    ...(canOpenInvoices(permissions) ? (['history', 'sentEmails'] as const) : []),
    // Accounting: did each invoice reach the books — accounting access only.
    ...(canSeeAccounting(permissions) ? (['accounting'] as const) : []),
    ...(advisorPerms(permissions).canAccessAdvisor ? (['aiCheckup'] as const) : []),
  ];
}

/** Invoicing's result lists (Invoices, Emails, Accounting), which a location's Invoicing shows too. */
export type ResultTabId = 'history' | 'sentEmails' | 'accounting';
const RESULT_TAB_IDS: readonly ResultTabId[] = ['history', 'sentEmails', 'accounting'];

/** The result lists this viewer has, in order. */
export function resultTabsFor(permissions: string[] = []): ResultTabId[] {
  const ids = invoicingSectionIds(permissions);
  return RESULT_TAB_IDS.filter((id) => ids.includes(id));
}

export const isResultTab = (id: string): id is ResultTabId => (RESULT_TAB_IDS as readonly string[]).includes(id);

/** A location's Invoicing tabs: Setup (the store's Who gets what) for rule
 *  editors, then the result lists this viewer has, for that store. */
export type LocationInvoicingTabId = 'setup' | ResultTabId;
export function locationInvoicingTabs(permissions: string[] = [], isSystemAdmin = false): LocationInvoicingTabId[] {
  const setup = isSystemAdmin || canSeeInvoicingRules(permissions);
  return [...(setup ? (['setup'] as const) : []), ...resultTabsFor(permissions)];
}

/** Whether a location's Invoicing has anything for this viewer. */
export const canOpenStoreInvoicing = (permissions: string[] = [], isSystemAdmin = false): boolean =>
  locationInvoicingTabs(permissions, isSystemAdmin).length > 0;

const ALIASES: Record<string, InvoicingSectionId> = {
  deliveries: 'deliveryLists',
  'delivery-lists': 'deliveryLists',
  invoices: 'history',
  'sent-emails': 'sentEmails',
  emails: 'sentEmails',
  sent: 'sentEmails',
  'accounting-sync': 'accounting',
  sync: 'accounting',
  quickbooks: 'accounting',
  netsuite: 'accounting',
  // Delivery status is gone: its email results are on Emails.
  deliveryHealth: 'sentEmails',
  'delivery-health': 'sentEmails',
  'delivery-status': 'sentEmails',
  health: 'sentEmails',
  advisor: 'aiCheckup',
  checkup: 'aiCheckup',
  'check-up': 'aiCheckup',
  'invoice-emails': 'contacts',
  'who-gets-what': 'whoGetsWhat',
  'coverage-map': 'coverage',
  map: 'coverage',
};

/** A requested section id (or alias) as an Invoicing section id, or undefined. */
export function resolveInvoicingSection(requested: string | undefined): InvoicingSectionId | undefined {
  if (!requested) return undefined;
  // Own keys only: values come from URLs, and `constructor` or `toString` must
  // not match an Object.prototype member.
  const own = (map: object) => Object.prototype.hasOwnProperty.call(map, requested);
  if (own(INVOICING_SECTION_LABELS)) return requested as InvoicingSectionId;
  return own(ALIASES) ? ALIASES[requested] : undefined;
}

/**
 * A link into Invoicing that asks for more than a tab — from a location's
 * Invoicing (which opens the account's) or the Overview:
 *
 *  - `deliveryList:<id>` / `schedule:<id>`: that rule's editor, over the tab
 *  - `contacts:<email>`: Contacts, searched to that address
 *  - `emails-needs-attention`: Emails, showing what needs attention
 *
 * They travel where a tab id does (a link's sub-tab, a settings jump).
 */
export interface InvoicingTarget {
  section?: InvoicingSectionId;
  rule?: { kind: 'schedule' | 'deliveryList'; id: string };
  contactSearch?: string;
  emailsResult?: 'needs_attention';
}

export const EMAILS_NEEDS_ATTENTION = 'emails-needs-attention';
export const invoicingRuleLink = (kind: 'schedule' | 'deliveryList', id: string): string => `${kind}:${id}`;
export const invoicingContactLink = (email: string): string => `contacts:${email}`;

/** A deep link (above) as what it asks for; undefined for a plain tab or nothing. */
function deepInvoicingTarget(requested: string): InvoicingTarget | undefined {
  if (requested === EMAILS_NEEDS_ATTENTION) return { section: 'sentEmails', emailsResult: 'needs_attention' };
  const contact = /^contacts:(.+)$/.exec(requested);
  if (contact) return { section: 'contacts', contactSearch: contact[1] };
  // A rule by its id only (a UUID): anything else in a link is ignored.
  const rule = /^(deliveryList|schedule):([0-9a-f-]{36})$/i.exec(requested);
  if (!rule) return undefined;
  return { rule: { kind: rule[1] as 'schedule' | 'deliveryList', id: rule[2]! } };
}

/** Whether a requested Invoicing tab is a deep link (kept whole in a settings link). */
export const isInvoicingDeepLink = (requested: string | undefined): boolean => !!requested && !!deepInvoicingTarget(requested);

/** What a requested tab id, alias or deep link asks Invoicing to show. */
export function resolveInvoicingTarget(requested: string | undefined): InvoicingTarget {
  if (!requested) return {};
  return deepInvoicingTarget(requested) ?? { section: resolveInvoicingSection(requested) };
}
